<template>
  <div>
    <div v-if="itemsPerPageSelect || tableExport" class="row my-2 mx-0">
      <div class="col-sm-6 d-flex align-items-center p-0" v-if="tableExport">
        <slot name="export">
          <button type="button" class="btn btn-success text-white text-white" @click="exportBtn" :disabled="exporting">
            {{ exporting ? 'Exporting ....' : 'Export to Excel' }}
          </button>
        </slot>
      </div>
      <div v-if="itemsPerPageSelect" class="col-sm-6 p-0" :class="{ 'offset-sm-6': !tableExport }">
        <div class="d-flex align-items-center justify-content-sm-end">
          <label class="me-2">Items per page:</label>
          <select class="form-control w-auto" @change="paginationChange">
            <option value="" selected disabled hidden>
              {{ perPageItems }}
            </option>
            <option v-for="(number, key) in [5, 10, 20, 50]" :val="number" :key="key">
              {{ number }}
            </option>
          </select>
          <button type="button" class="btn btn-outline-dark btn-sm m-1" @click="resetBtn">
            <CIcon :icon="cilFilter" /><b title="Reset"
              style="font-size:smaller;position:relative; left:-4px;bottom: -5px;">X</b>
          </button>
        </div>
      </div>
    </div>

    <slot name="over-table" />
    <div :class="`position-relative ${responsive ? 'table-responsive' : ''}`">
      <table :class="tableClasses">
        <thead>
          <tr>
            <template v-for="(name, index) in columnNames" :key="index">
              <th @click="changeSort(rawColumnNames[index], index)" :class="[headerClass(index), sortingIconStyles]"
                :style="headerStyles(index)" class="bg-light" style="border: none">
                <slot :name="`${rawColumnNames[index]}-header`">
                  <div class="d-inline">{{ name }}</div>
                </slot>
                <slot v-if="isSortable(index)" name="sorting-icon" :state="getIconState(index)">
                  <CIcon width="18" :content="$options.icons.cilSortAscending" :class="iconClasses(index)" />
                </slot>
              </th>
            </template>
          </tr>

          <tr v-if="columnFilter" class="table-sm">
            <template v-for="(colName, index) in rawColumnNames" :key="index">
              <th :class="headerClass(index)" class="bg-light">
                <slot :name="`${rawColumnNames[index]}-filter`">
                  <select class="form-control table-filter" v-model="columnFilterState[colName]"
                    v-if="fields[index].selection != null && fields[index].filter === false"
                    @change="columnFilterEvent(colName, $event.target.value, 'change')">
                    <option value=""></option>
                    <option v-for="option in fields[index].selection" v-bind:value="option.value"
                      v-bind:key="option.value">
                      {{ option.label }}
                    </option>
                  </select>
                  <div v-else-if="fields[index].datetime === true && fields[index].filter === false"
                    class="d-inline-flex w-100">
                    <input @click="calendarColumnClicked(colName)" :value="calendarData[colName].val"
                      class="w-100 table-filter form-control" />
                    <CModal :title="'Filter by ' + fields[index].label + ' Range'" color="calendar"
                      v-model:show="showDatePicker[colName]" @okClicked="datePickerClicked(colName)">
                      <div class="row col-12 ms-1">
                        From
                      </div>
                      <div class="my-3 row">
                        <div class="form-group col-6">
                          <VueDatePicker v-model="calendarData[colName].startDate" model-type="y-MM-dd" format="dd/MM/y"
                            :time-config="{ enableTimePicker: false }" />
                        </div>
                        <div class="form-group col-6">
                          <VueDatePicker v-model="calendarData[colName].startTime" model-type="HH:mm:00" time-picker
                            :time-config="{ is24: false }" @click:input="startDateChanged(colName)" />
                        </div>
                      </div>
                      <div class="row col-12 ms-1">
                        Until
                      </div>
                      <div class="my-3 row">
                        <div class="form-group col-6">
                          <VueDatePicker v-model="calendarData[colName].endDate" model-type="y-MM-dd" format="dd/MM/y"
                            :time-config="{ enableTimePicker: false }" />
                        </div>
                        <div class="form-group col-6">
                          <VueDatePicker v-model="calendarData[colName].endTime" model-type="HH:mm:59" time-picker
                            :time-config="{ is24: false }" />
                        </div>
                      </div>
                    </CModal>
                  </div>
                  <input v-else-if="!fields || fields[index].filter !== false" class="w-100 table-filter form-control"
                    @input="columnFilterEvent(colName, $event.target.value, 'input')"
                    @change="columnFilterEvent(colName, $event.target.value, 'change')"
                    :value="columnFilterState[colName]" />
                </slot>
              </th>
            </template>
          </tr>
        </thead>


        <tbody :style="clickableRows ? 'cursor:pointer;' : null" class="position-relative">
          <template v-for="(item, itemIndex) in currentItems" :key="itemIndex">
            <tr :class="item._classes" :tabindex="clickableRows ? 0 : null"
              @click="rowClicked(item, itemIndex + firstItemIndex)">
              <template v-for="(colName, index) in rawColumnNames">
                <slot v-if="$slots[colName]" :name="colName" :item="item" :index="itemIndex + firstItemIndex" />
                <td v-else :class="cellClass(item, colName, index)" :key="index">
                  {{ String(item[colName]) }}
                </td>
              </template>
            </tr>
            <tr v-if="$slots.details" class="p-0" style="border:none !important" :key="'details' + itemIndex">
              <td :colspan="colspan" class="p-0" style="border:none !important">
                <slot name="details" :item="item" :index="itemIndex + firstItemIndex" />
              </td>
            </tr>
          </template>
          <tr v-if="!currentItems.length">
            <td :colspan="colspan">
              <slot name="no-items-view">
                <div class="text-center my-5">
                  <h2>
                    {{ passedItems.length ? 'No filtering results ' : 'No items' }}
                    <CIcon width="30" :content="$options.icons.cilBan" class="text-danger mb-2" />
                  </h2>
                </div>
              </slot>
            </td>
          </tr>
        </tbody>

        <tfoot v-if="footer && currentItems.length > 0">
          <tr>
            <template v-for="(name, index) in columnNames" :key="index">
              <th @click="changeSort(rawColumnNames[index], index)" :class="[headerClass(index), sortingIconStyles]"
                :style="headerStyles(index)">
                <slot :name="`${rawColumnNames[index]}-header`">
                  <div class="d-inline">{{ name }}</div>
                </slot>
                <slot v-if="isSortable(index)" name="sorting-icon" :state="getIconState(index)">
                  <CIcon width="18" :content="$options.icons.cilArrowTop" :class="iconClasses(index)" />
                </slot>
              </th>
            </template>
          </tr>
        </tfoot>
        <slot name="caption" />
      </table>

      <slot name="loading" v-if="loading">
        <div style="position:absolute;left:0;top:0;bottom:0;right:0;background-color:rgb(255,255,255,0.4);">
          <div style="position:absolute;bottom:50%;left:50%;transform:translateX(-50%);">
            <CSpinner color="success" />
          </div>
        </div>
      </slot>

    </div>
    <slot name="under-table">
      <div class="row">
        <div class="col-md-6">
          <CPagination v-if="pagination" v-show="totalPages > 1" :activePage="page" :pages="totalPages"
            v-bind="typeof pagination === 'object' ? pagination : null" @update:activePage="reload"
            :limit="paginationSize" :doubleNextArrows="totalPages <= 100" />
        </div>
        <div class="col-md-6 d-flex justify-content-end" v-if="totalItems > 0">
          Total number of records: {{ totalItems }}
        </div>
      </div>
    </slot>
  </div>
</template>

<script>
import { CSpinner } from '@coreui/vue'
import { CIcon } from '@coreui/icons-vue'
import { cilArrowTop, cilBan, cilSortAscending, cilSave, cilFilter } from '@coreui/icons'
import { VueDatePicker } from "@vuepic/vue-datepicker"
import apiHandler from '@/helpers/apiHandler'
import utils from '@/helpers/utils'
import CModal from '@/views/components/CModal'
import CPagination from '@/views/components/CPagination'
import '@vuepic/vue-datepicker/dist/main.css'

export default {
  name: 'CDataTable',
  icons: { cilArrowTop, cilBan, cilSortAscending, cilSave },
  components: {
    CPagination,
    CSpinner,
    CIcon,
    CModal,
    VueDatePicker
  },
  props: {
    fields: Array,
    itemsPerPage: {
      type: Number,
      default: 10
    },
    activePage: Number,
    pagination: [Boolean, Object],
    addTableClasses: [String, Array, Object],
    responsive: {
      type: Boolean,
      default: true
    },
    size: String,
    dark: Boolean,
    striped: Boolean,
    fixed: Boolean,
    hover: Boolean,
    border: Boolean,
    outlined: Boolean,
    itemsPerPageSelect: Boolean,
    sorter: [Boolean, String],
    tableFilter: [Boolean, String],
    tableExport: [Boolean, String],
    columnFilter: [Boolean, String],
    sorterValue: {
      type: Object,
      default: () => { return { column: 'created_date', asc: false } }
    },
    tableFilterValue: String,
    columnFilterValue: Object,
    preFilterValue: Object,
    footer: Boolean,
    loading: Boolean,
    clickableRows: Boolean,
    url: String
  },
  data() {
    return {
      items: [],
      tableFilterState: this.tableFilterValue,
      columnFilterState: {},
      sorterState: {
        column: undefined,
        asc: true
      },
      page: this.activePage || 1,
      perPageItems: this.itemsPerPage,
      passedItems: this.items || [],
      totalItems: 0,
      totalPages: 1,
      calendarData: [],
      showDatePicker: [],
      exporting: false,
      width: 1024,
      cilFilter
    }
  },
  created() {
    this.initCalendarData()
    this.reload()
    window.addEventListener('resize', this.handleResize)
  },
  watch: {
    sorterValue: {
      immediate: true,
      handler(val) {
        this.sorterState.column = val.column
        this.sorterState.asc = val.asc === false ? false : true
      }
    },
    tableFilterValue(val) {
      this.tableFilterState = val
    },
    columnFilterValue: {
      immediate: true,
      handler(val) {
        this.columnFilterState = Object.assign({}, val)
      }
    },
    items(val, oldVal) {
      if (
        val.length !== oldVal.length ||
        JSON.stringify(val) !== JSON.stringify(oldVal)
      ) {
        this.passedItems = val
      }
    },
    totalPages: {
      immediate: true,
      handler(val) {
        this.$emit('pages-change', val)
      }
    }
  },
  computed: {
    paginationSize() {
      return this.width > 800 ? 10 : 5
    },
    columnFiltered() {
      let items = this.passedItems.slice()
      return items
    },
    filterableCols() {
      return this.rawColumnNames.filter(name => {
        return this.generatedColumnNames.includes(name)
      })
    },
    datetimeCols() {
      return this.fields.filter(field => {
        return field.datetime === true
      }).map(field => field.key)
    },
    tableFiltered() {
      let items = this.columnFiltered.slice()
      if (this.tableFilter === true && this.tableFilterState) {
        const filter = this.tableFilterState.toLowerCase()
        const hasFilter = (item) => String(item).toLowerCase().includes(filter)
        items = items.filter(item => {
          return this.filterableCols.filter(key => hasFilter(item[key])).length
        })
      }
      return items
    },
    sortedItems() {
      const col = this.sorterState.column
      if (!col || this.sorter !== true || !this.rawColumnNames.includes(col)) {
        return this.tableFiltered
      }

      const flip = this.sorterState.asc ? 1 : -1
      if (this.datetimeCols.includes(col)) {
        // handle datetime sorting due to different date format
        return this.tableFiltered.slice().sort((a, b) => {
          const aDate = utils.toDateTimeObj(a[col])
          const bDate = utils.toDateTimeObj(b[col])
          return (aDate > bDate) ? 1 * flip : ((bDate > aDate) ? -1 * flip : 0)
        })
      } else {
        // if values in column are to be sorted by numeric value they all have to be type number
        return this.tableFiltered.slice().sort((a, b) => {
          return (a[col] > b[col]) ? 1 * flip : ((b[col] > a[col]) ? -1 * flip : 0)
        })
      }
    },
    firstItemIndex() {
      return (this.computedPage - 1) * this.perPageItems || 0
    },
    paginatedItems() {
      return this.sortedItems.slice(
        this.firstItemIndex,
        this.firstItemIndex + this.perPageItems
      )
    },
    currentItems() {
      return this.sortedItems
    },
    // totalPages () {
    //   return Math.ceil((this.sortedItems.length)/ this.perPageItems) || 1
    // },
    computedPage() {
      return this.pagination ? this.page : this.activePage
    },
    generatedColumnNames() {
      return Object.keys(this.passedItems[0] || {}).filter(el => el.charAt(0) !== '_')
    },
    rawColumnNames() {
      if (this.fields) {
        return this.fields.map(el => el.key || el)
      }
      return this.generatedColumnNames
    },
    columnNames() {
      if (this.fields) {
        return this.fields.map(f => {
          return f.label !== undefined ? f.label : this.pretifyName(f.key || f)
        })
      }
      return this.rawColumnNames.map(el => this.pretifyName(el))
    },
    tableClasses() {
      return [
        'table',
        this.addTableClasses,
        {
          [`table-${this.size}`]: this.size,
          'table-dark': this.dark,
          'table-striped': this.striped,
          'b-table-fixed': this.fixed,
          'table-hover': this.hover,
          'table-bordered': this.border,
          'border': this.outlined
        }
      ]
    },
    sortingIconStyles() {
      return { 'position-relative pe-4': this.sorter }
    },
    colspan() {
      return this.rawColumnNames.length
    }
  },
  filters: {
    formatStartVal: function (value) {
      if (value == '') {
        return "Start"
      } else {
        return utils.dateFormat(value)
      }
    },
    formatEndVal: function (value) {
      if (value == '') {
        return "End"
      } else {
        return utils.dateFormat(value)
      }
    }
  },
  methods: {
    calendarColumnClicked(colName) {
      this.showDatePicker[colName] = true
    },
    initCalendarData() {
      for (let i = 0; i < this.datetimeCols.length; i++) {
        let colName = this.datetimeCols[i]
        this.calendarData[colName] = { 'startDate': '', 'startTime': '', 'endDate': '', 'endTime': '', 'val': '' }
        this.showDatePicker[colName] = false
      }
    },
    startDateChanged(colName) {
      if (this.calendarData[colName].startTime == '') {
        this.calendarData[colName].startTime = '00:00'
      }
    },
    endDateChanged(colName) {
      if (this.calendarData[colName].endTime == '') {
        this.calendarData[colName].endTime = '23:59'
      }
    },
    handleResize() {
      this.width = window.innerWidth
    },
    changeSort(column, index) {
      if (column && !this.isSortable(index)) {
        return
      }
      //if column changed or sort was descending change asc to true
      const state = this.sorterState
      state.asc = state.column !== column || !state.asc
      state.column = column
      this.$emit('update:sorter-value', this.sorterState)
      this.reload()
    },
    columnFilterEvent(colName, value, type) {

      this.setColumnFilter(colName, value)
      const e = type === 'input' ? 'column-filter-input' : 'update:column-filter-value'
      this.$emit(e, this.columnFilterState)

      if (e == 'update:column-filter-value') {
        this.reload()
      }
    },
    setColumnFilter(colName, value) {
      if (value == '') {
        delete this.columnFilterState[colName]
      } else {
        this.columnFilterState[colName] = value
      }
    },
    tableFilterChange(value, type = 'input') {
      this.tableFilterState = value
      const e = type === 'input' ? 'table-filter-input' : 'update:table-filter-value'
      this.$emit(e, this.tableFilterState)
    },
    pretifyName(name) {
      return name.replace(/[-_.]/g, ' ')
        .replace(/ +/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },
    cellClass(item, colName, index) {
      let classes = []
      if (item._cellClasses && item._cellClasses[colName]) {
        classes.push(item._cellClasses[colName])
      }
      if (this.fields && this.fields[index]._classes) {
        classes.push(this.fields[index]._classes)
      }
      return classes
    },
    isSortable(index) {
      return this.sorter && (!this.fields || this.fields[index].sorter !== false)
    },
    headerClass(index) {
      const fields = this.fields
      return fields && fields[index]._classes ? fields[index]._classes : ''
    },
    headerStyles(index) {
      let style = ''
      if (this.isSortable(index)) {
        style += `cursor:pointer;`
      }
      if (this.fields && this.fields[index] && this.fields[index]._style) {
        style += this.fields[index]._style
      }
      return style
    },
    rowClicked(item, index) {
      this.$emit('row-clicked', item, index)
    },
    getIconState(index) {
      const direction = this.sorterState.asc ? 'asc' : 'desc'
      return this.rawColumnNames[index] === this.sorterState.column ? direction : 0
    },
    iconClasses(index) {
      const state = this.getIconState(index)
      return [
        'icon-transition position-absolute arrow-position',
        {
          'transparent': !state,
          'rotate-icon': state === 'desc'
        }
      ]
    },
    paginationChange(e) {
      this.$emit('pagination-change', Number(e.target.value))
      this.perPageItems = Number(e.target.value)
      this.reload()
    },
    resetBtn() {
      this.columnFilterState = {}
      this.sorterState = this.sorterValue
      this.perPageItems = 10
      this.page = 1
      this.initCalendarData()
      this.reload()
    },
    exportBtn() {
      this.exporting = true

      const requestOptions = {
        headers: apiHandler.authHeader(),
        method: 'POST',
        body: JSON.stringify({
          sort_by: utils.snakeToCamel(this.sorterState.column),
          sort_asc: this.sorterState.asc,
          filters: { ...this.columnFilterState, ...this.preFilterValue }
        })
      };

      fetch(this.url + '/export',
        requestOptions
      ).then(apiHandler.handleResponse)
        .then(result => {
          if (!result.status) {
            const link = document.createElement('a')
            link.href = result
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
          }
          this.exporting = false
        })
        .catch(apiHandler.handleException)

    },
    reload(pageOnclick) {
      this.page = pageOnclick || 1

      const requestOptions = {
        headers: apiHandler.authHeader(),
        method: 'POST',
        body: JSON.stringify({
          page_no: this.page - 1,
          page_size: this.perPageItems,
          sort_by: utils.snakeToCamel(this.sorterState.column),
          sort_asc: this.sorterState.asc,
          filters: { ...this.columnFilterState, ...this.preFilterValue }
        })
      };

      if (this.url == null || this.url === '') {
        return []
      }

      return fetch(
        this.url,
        requestOptions
      ).then(response => {
        if (response.status == 200) {
          return response.text().then(text => {
            const resp = text && JSON.parse(text)

            return resp.data
          })
        } else if (response.status == 403) {
          location.href = '/403'
        } else if (response.status == 500) {
          location.href = '/500'
        }
      })
        .then(result => {
          const {
            content = [],
            total_elements = 0,
            total_pages = 1
          } = result || {}

          this.items = content
          this.totalItems = total_elements
          this.totalPages = total_pages
        })
        .catch(apiHandler.handleException);
    },
    datePickerClicked(colName) {
      var startDatetime = utils.dateFormat(this.calendarData[colName].startDate, this.calendarData[colName].startTime, '00')
      var endDatetime = utils.dateFormat(this.calendarData[colName].endDate, this.calendarData[colName].endTime, '59')

      var msg = "";
      if (startDatetime != "") {
        msg = msg + "From " + startDatetime
      }
      if (endDatetime != "") {
        msg = msg + " Until " + endDatetime
      }
      this.calendarData[colName].val = msg

      this.setColumnFilter(colName + '_start', startDatetime)
      this.setColumnFilter(colName + '_end', endDatetime)

      this.reload()
    },
    clearStartDate(colName) {
      this.calendarData[colName].startDate = ''
      this.calendarData[colName].startTime = ''
      this.setColumnFilter(colName, '')
    },
    clearEndDate(colName) {
      this.calendarData[colName].endDate = ''
      this.calendarData[colName].endTime = ''
      this.setColumnFilter(colName, '')
    },
    clearStartTime(colName) {
      this.calendarData[colName].startTime = ''
      let colVal = utils.dateFormat(this.calendarData[colName].startDate, this.calendarData[colName].startTime, '00')
      this.setColumnFilter(colName, colVal)
    },
    clearEndTime(colName) {
      this.calendarData[colName].endTime = ''
      let colVal = utils.dateFormat(this.calendarData[colName].endDate, this.calendarData[colName].endTime, '00')
      this.setColumnFilter(colName, colVal)
    }
  }
}
</script>

<style scoped>
.transparent {
  opacity: 0.4;
}

.icon-transition {
  -webkit-transition: transform 0.3s;
  transition: transform 0.3s;
}

.arrow-position {
  right: 0;
  top: 50%;
  -ms-transform: translateY(-50%);
  transform: translateY(-50%);
}

.rotate-icon {
  -ms-transform: translateY(-50%) rotate(-180deg);
  transform: translateY(-50%) rotate(-180deg);
}
</style>
